<script setup lang="ts">
import { nextTick, computed, Ref, inject, ref, onBeforeUnmount, onMounted, watch, defineProps } from 'vue';
import { RouterLink, useRouter, useRoute } from 'vue-router'
import { cn } from '@/lib/utils'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import MediaThumb from '@/components/MediaThumb.vue';
import { Media, ExternalIdProviders, ExternalId } from '@/types/Media';
import permissions from "@/services/permissions";
import { Member } from '@/types/Member'
import { TransferDestination } from '@/types/Transfer'
import { Filters } from '@/types/Filters'
import { eventBus } from '@/services/eventBus';
import { useMedia } from '@/services/Media';
import { useGroups } from '@/services/Groups';
import { useChannels } from '@/services/Channels';
import { useArrayOperations } from '@/services/useArrayOperations';
import { useTransitionAnimations } from '@/services/useTransitionAnimations';
import { formatDistanceToNow, parse, format } from 'date-fns';
import { toast } from 'vue-sonner';
import { useCookies } from '@vueuse/integrations/useCookies';
import {
	CircleCheck, CircleX, Trash2, ArrowDownToLine,
	Send, Image as ImageIcon, Folder, UserRound, ArrowUpFromLine,
	LayoutGrid, EllipsisVertical, Rss, Loader2, Eye,
	Sparkles, FolderOpen, MoreHorizontal,
	ThumbsUp, ThumbsDown, Grid3X3, Ellipsis, Check, X
} from 'lucide-vue-next'

const { fetchMedia, trashFile, setModerationStatus } = useMedia();
const { groupTree, getGroupTree, addMediaToGroup } = useGroups();
const { channelList, getChannels, addMediaToChannel } = useChannels();
const { onBeforeEnter, onLeave, onEnter, cleanupAnimations } = useTransitionAnimations();
const { array: media, add, reset, removeById } = useArrayOperations<Media>();

const router = useRouter();
const route = useRoute();
const userInfo = inject<Ref<Member> | null>('userInfo', null);
const vhostId = inject<Ref<string> | null>('vhostId', null);
const removedIds = ref<number[]>([]);
const { checkPermission } = permissions();
const dragPreview = ref<HTMLDivElement | null>(null);
const loading = ref(true);
const loadingMore = ref(false);
const loadMore = ref<number>(0);
const totalMedia = ref(0);
const contentCount = ref(0)
const fields: (keyof Media)[] = ['id', 'moderationStatus', 'groups', 'parent_info', 'thumbUrl', 'publicUrl', 'title', 'message', 'upload', 'filetype', 'extension', 'externalids', 'status', 'metadata', 'moderationStatus', 'contenttype', 'uid', 'user_firstname', 'user_email', 'tags', 'hits', 'channel'];
const pageNum = ref(0);
const pageSize = 24;
const modOffSet = 0;
const transferList = inject<Ref<TransferDestination[]> | []>('transferList', []);

// Infinite scroll refs
const scrollSentinel = ref<HTMLDivElement | null>(null);
const listScrollSentinel = ref<HTMLDivElement | null>(null);
let observer: IntersectionObserver | null = null;

// Scroll position restoration
const scrollContainer = ref<HTMLElement | null>(null);

const props = defineProps<{
	filters: Filters;
	type: string;
	defaultProfile: string;
	sortOrder: string | null;
	vhostId: string | null;
	modelValue: Media[];
	mobileView?: 'swipe' | 'scroll';
}>();
const emit = defineEmits<{
	(event: 'update-dragging', status: boolean): void;
	(event: 'set-current-count', count: number): void;
	(event: 'set-total-count', count: number): void;
	(event: 'update:modelValue', value: Media[]): void;
}>();

const selectedMedia = ref<Media[]>(props.modelValue);
const draggedItemId = ref(0);

const isSelected = (id: number) => selectedMedia.value.some((media) => media.id === id);
const isBeingRemoved = (id: number) => removedIds.value.includes(id);
const removingItems = ref(new Set<number>());

const mmMode = inject<Ref<boolean>>('mmMode', ref(false));
const isMM = computed(() => mmMode.value)

// --- Cache key for scroll position & media state ---
const getCacheKey = () => {
	const filterStr = JSON.stringify(props.filters);
	const sort = props.sortOrder || 'upload DESC';
	return `media_cache_${props.vhostId}_${sort}_${filterStr}`;
};

const saveScrollState = () => {
	try {
		const container = findScrollParent();
		if (!container || media.value.length === 0) return;
		const state = {
			scrollTop: container.scrollTop,
			media: media.value,
			totalMedia: totalMedia.value,
			pageNum: pageNum.value
		};
		sessionStorage.setItem(getCacheKey(), JSON.stringify(state));
	} catch (e) { /* quota exceeded or similar */ }
};

const restoreScrollState = (): boolean => {
	try {
		const cached = sessionStorage.getItem(getCacheKey());
		if (!cached) return false;
		const state = JSON.parse(cached);
		if (!state.media || state.media.length === 0) return false;

		reset();
		add(state.media);
		totalMedia.value = state.totalMedia;
		pageNum.value = state.pageNum;
		loading.value = false;

		emit('set-total-count', totalMedia.value);
		emit('set-current-count', media.value.length);

		// Restore scroll position after DOM updates
		nextTick(() => {
			const container = findScrollParent();
			if (container) {
				container.scrollTop = state.scrollTop;
			}
		});
		return true;
	} catch (e) { return false; }
};

const findScrollParent = (): HTMLElement | null => {
	if (scrollContainer.value) return scrollContainer.value;
	// Walk up from our component root to find the overflow-y-auto container
	let el = document.querySelector('.flex-1.overflow-y-auto') as HTMLElement;
	if (el) {
		scrollContainer.value = el;
		return el;
	}
	return null;
};

// --- Moderation ---
const moderateMedia = async (mediaId: number, status: number, note?: string) => {
	removingItems.value.add(mediaId);
	const result = await setModerationStatus(mediaId, status, note);
	if (result) {
		await new Promise((resolve) => setTimeout(resolve, 300));
		removeById(mediaId);
		removingItems.value.delete(mediaId);
		// Advance to next card in swipe mode
		if (swipeIndex.value >= swipeableMedia.value.length) {
			swipeIndex.value = Math.max(0, swipeableMedia.value.length - 1);
		}
	}
};
const trashMedia = async (id: number, recursive: boolean) => {
	await trashFile(id, recursive);
};

// --- Drag & drop ---
const handleDragStart = (mediaItem: Media, event: DragEvent) => {
	draggedItemId.value = mediaItem.id;
	eventBus.emit('drag-start', mediaItem);
	emit('update-dragging', true);
	event.dataTransfer?.setData('application/json', JSON.stringify(selectedMediaObjects.value));
	const transparentImage = new Image();
	transparentImage.src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAwAB/6GcG8AAAAAASUVORK5CYII=";
	event.dataTransfer?.setDragImage(transparentImage, 0, 0);
	if (selectedMedia.value.length == 0) {
		selectedMedia.value.push(mediaItem)
	}
	if (dragPreview.value) {
		dragPreview.value.style.opacity = '0.5';
		dragPreview.value.style.left = `${event.clientX}px`;
		dragPreview.value.style.top = `${event.clientY}px`;
	}
	document.addEventListener('dragover', updateDragPreviewPosition);
};

const onDragEnd = () => {
	emit('update-dragging', false);
	draggedItemId.value = 0;
	if (dragPreview.value) {
		dragPreview.value.style.opacity = '0';
	}
	document.removeEventListener('dragover', updateDragPreviewPosition);
};

const updateDragPreviewPosition = (event: DragEvent) => {
	if (dragPreview.value) {
		dragPreview.value.style.left = `${event.clientX}px`;
		dragPreview.value.style.top = `${event.clientY}px`;
	}
};

// --- Helpers ---
function truncateText(text: string, maxLength: number): string {
	if (!text || text.length <= maxLength) return text;
	return text.substring(0, maxLength) + '...';
}
function stripHtml(input: string): string {
	const parser = new DOMParser();
	const doc = parser.parseFromString(input, 'text/html');
	return doc.body.textContent || '';
}

const timeCookies = useCookies(['timeFormat']);
const timeFormatMode = ref<'relative' | 'absolute'>(timeCookies.get('timeFormat') === 'absolute' ? 'absolute' : 'relative');

const toggleTimeFormat = () => {
	timeFormatMode.value = timeFormatMode.value === 'relative' ? 'absolute' : 'relative';
	timeCookies.set('timeFormat', timeFormatMode.value, { path: '/', maxAge: 60 * 60 * 24 * 365 });
};

const parseDate = (date: string | number): Date | null => {
	if (!date) return null
	let targetDate: Date
	if (typeof date === 'number' || /^\d{10,13}$/.test(String(date))) {
		const ts = Number(date)
		targetDate = new Date(ts > 9999999999 ? ts : ts * 1000)
	} else {
		targetDate = parse(String(date), 'yyyy-MM-dd HH:mm:ss', new Date())
		if (isNaN(targetDate.getTime())) {
			targetDate = new Date(date)
		}
	}
	return isNaN(targetDate.getTime()) ? null : targetDate
};

const formatRelativeTime = (date: string | number): string => {
	const d = parseDate(date)
	if (!d) return 'unknown'
	return formatDistanceToNow(d, { addSuffix: false })
};

const formatAbsoluteTime = (date: string | number): string => {
	const d = parseDate(date)
	if (!d) return 'unknown'
	return format(d, 'MMM d, yyyy h:mm a')
};

const formatTime = (date: string | number): string => {
	return timeFormatMode.value === 'absolute' ? formatAbsoluteTime(date) : formatRelativeTime(date)
};

const fileTypeLabel = (filetype: number) => {
	switch (filetype) {
		case 1: return 'Image'
		case 2: return 'Video'
		case 3: return 'Audio'
		case 4: return 'Document'
		default: return 'File'
	}
}

const selectedMediaObjects = computed(() => {
	return selectedMedia.value.map((smedia) =>
		media.value.find((m) => m.id == smedia.id)
	).filter((m) => m);
});

// --- Infinite scroll ---
const loadNextPage = () => {
	if (loadingMore.value || loading.value) return;
	if (media.value.length >= totalMedia.value && totalMedia.value > 0) return;
	loadMore.value = 1;
};

const setupIntersectionObserver = () => {
	if (observer) observer.disconnect();
	observer = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				loadNextPage();
			}
		});
	}, { rootMargin: '400px' });

	nextTick(() => {
		if (scrollSentinel.value) observer?.observe(scrollSentinel.value);
		if (listScrollSentinel.value) observer?.observe(listScrollSentinel.value);
	});
};

// --- Data fetching ---
watch(() => loadMore.value, (newValue) => {
	if (newValue === 1) {
		pageNum.value += 1;
		loadingMore.value = true;
		if (vhostId && vhostId.value) { getFiles(vhostId.value); }
	}
});
watch(() => props.modelValue, (newValue) => { selectedMedia.value = newValue; });
watch(() => props.filters, () => { if (props.vhostId) { getFiles(props.vhostId); } }, { deep: true });
watch(() => props.sortOrder, () => { if (vhostId && vhostId.value) { getFiles(vhostId.value); } });

const toggleSelected = (media: Media) => {
	if (isSelected(media.id)) {
		updateSelectedMedia(selectedMedia.value.filter((item) => item.id !== media.id));
	} else {
		updateSelectedMedia([...selectedMedia.value, media]);
	}
};

const updateSelectedMedia = (newMedia: Media[]) => {
	selectedMedia.value = newMedia;
	emit('update:modelValue', newMedia);
};

const copyShareLink = async (mediaItem: Media, type: string) => {
	const baseUrl = mediaItem.publicUrl || `${window.location.origin}/media/${mediaItem.id}`
	const link = type === 'review'
		? `${window.location.origin}/media/${mediaItem.id}`
		: baseUrl
	try {
		await navigator.clipboard.writeText(link)
		toast.success(type === 'review' ? 'Review link copied' : 'Asset link copied')
	} catch {
		toast.error('Failed to copy link')
	}
};

// Save scroll position before navigating away
const handleBeforeUnload = () => saveScrollState();

onMounted(() => {
	dragPreview.value = document.querySelector('.drag-preview') as HTMLDivElement;

	// Try to restore cached state first (coming back from media detail)
	if (props.vhostId && restoreScrollState()) {
		nextTick(setupIntersectionObserver);
		return;
	}

	if (props.vhostId) {
		getFiles(props.vhostId);
	}
	setupIntersectionObserver();
});

onBeforeUnmount(() => {
	saveScrollState();
	document.removeEventListener('dragover', updateDragPreviewPosition);
	cleanupAnimations();
	if (observer) observer.disconnect();
});

// Also save on route change (navigating to media detail)
watch(() => route.path, () => { saveScrollState(); });

const getFiles = async (vhost: string) => {
	try {
		const fixedFilters = Object.fromEntries(
			Object.entries(props.filters).filter(([_, value]) => value !== null && value !== undefined && value !== '')
		);
		if (typeof fixedFilters.channel !== 'undefined' && fixedFilters.channel === 0) {
			delete fixedFilters.channel;
		}
		loading.value = true;

		// Clear existing results immediately when not loading more (e.g. tab/filter change)
		if (loadMore.value !== 1) {
			reset();
			totalMedia.value = 0;
			pageNum.value = 0;
			// Clear cached state for old filters
			try { sessionStorage.removeItem(getCacheKey()); } catch (e) {}
		}

		const sortOrder = props.sortOrder || 'UPLOAD DESC';
		const { mediaData, totalCount } = await fetchMedia(
			vhostId ? parseInt(vhostId.value) : 0,
			fixedFilters,
			sortOrder,
			pageNum.value * pageSize - modOffSet,
			pageSize,
			fields,
			{ want_status: 1 }
		);
		totalMedia.value = totalCount;
		if (loadMore.value === 1) {
			add(mediaData);
			loadMore.value = 0;
			loadingMore.value = false;
		} else {
			add(mediaData);
		}
		emit('set-total-count', totalMedia.value);
		emit('set-current-count', media.value.length);

		nextTick(setupIntersectionObserver);
	} catch (error) {
		console.error('Error fetching media:', error);
	} finally {
		loading.value = false;
		loadingMore.value = false;
	}
};

// ========================================
// MOBILE SWIPE (TINDER) MODE
// ========================================
const isMobile = ref(false);
const checkMobile = () => { isMobile.value = window.innerWidth < 768; };

const swipeIndex = ref(0);
const swipeX = ref(0);
const swipeY = ref(0);
const swipeStartX = ref(0);
const swipeStartY = ref(0);
const swiping = ref(false);
const swipeDirection = ref<string | null>(null);
const showActionsMenu = ref(false);

const swipeableMedia = computed(() => media.value.filter(m => !removingItems.value.has(m.id)));
const currentSwipeItem = computed(() => swipeableMedia.value[swipeIndex.value] || null);
const nextSwipeItem = computed(() => swipeableMedia.value[swipeIndex.value + 1] || null);

const swipeThreshold = 80;

const onTouchStart = (e: TouchEvent) => {
	if (showActionsMenu.value) return;
	swiping.value = true;
	swipeStartX.value = e.touches[0].clientX;
	swipeStartY.value = e.touches[0].clientY;
	swipeX.value = 0;
	swipeY.value = 0;
	swipeDirection.value = null;
};

const onTouchMove = (e: TouchEvent) => {
	if (!swiping.value) return;
	swipeX.value = e.touches[0].clientX - swipeStartX.value;
	swipeY.value = e.touches[0].clientY - swipeStartY.value;

	// Determine direction
	const absX = Math.abs(swipeX.value);
	const absY = Math.abs(swipeY.value);
	if (absX > 20 || absY > 20) {
		if (absX > absY) {
			swipeDirection.value = swipeX.value > 0 ? 'right' : 'left';
		} else {
			swipeDirection.value = swipeY.value < 0 ? 'up' : 'down';
		}
	}
};

const onTouchEnd = () => {
	if (!swiping.value || !currentSwipeItem.value) {
		swiping.value = false;
		return;
	}

	const absX = Math.abs(swipeX.value);
	const absY = Math.abs(swipeY.value);

	if (absX > swipeThreshold && absX > absY) {
		if (swipeX.value > 0) {
			// Swipe RIGHT → Approve
			moderateMedia(currentSwipeItem.value.id, 1);
		} else {
			// Swipe LEFT → Deny
			moderateMedia(currentSwipeItem.value.id, 2);
		}
	} else if (absY > swipeThreshold && absY > absX) {
		if (swipeY.value < 0) {
			// Swipe UP → Categorize (show group/channel menu)
			showActionsMenu.value = true;
			swipeActionMode.value = 'categorize';
		} else {
			// Swipe DOWN → More actions
			showActionsMenu.value = true;
			swipeActionMode.value = 'actions';
		}
	}

	swiping.value = false;
	swipeX.value = 0;
	swipeY.value = 0;
	swipeDirection.value = null;
};

const swipeActionMode = ref<'categorize' | 'actions'>('actions');
const categorizeLoading = ref(false);
const assigningTo = ref<number | null>(null);

const flattenGroups = (groups: any[], depth = 0): { id: number; name: string; depth: number }[] => {
	const result: { id: number; name: string; depth: number }[] = [];
	for (const g of groups) {
		result.push({ id: g.id, name: g.name, depth });
		if (g.children?.length) {
			result.push(...flattenGroups(g.children, depth + 1));
		}
	}
	return result;
};

const flatGroups = computed(() => flattenGroups(groupTree.value));

const flattenChannels = (channels: any[], depth = 0): { id: number; name: string; depth: number }[] => {
	const result: { id: number; name: string; depth: number }[] = [];
	for (const c of channels) {
		result.push({ id: c.id, name: c.name, depth });
		if (c.children?.length) {
			result.push(...flattenChannels(c.children, depth + 1));
		}
	}
	return result;
};

const flatChannels = computed(() => flattenChannels(channelList.value));

const getChannelName = (channelId: number) => {
	const ch = flatChannels.value.find((c) => {
		return c.id === channelId
	})

	return ch?.name ?? null
};

const handleAddToGroup = async (groupId: number) => {
	if (!currentSwipeItem.value || assigningTo.value) return;
	assigningTo.value = groupId;
	try {
		await addMediaToGroup(groupId, currentSwipeItem.value.id);
		toast.success('Added to group');
		dismissActionsMenu();
	} catch (e) {
		toast.error('Failed to add to group');
	} finally {
		assigningTo.value = null;
	}
};

const handleAddToChannel = async (channelId: number) => {
	if (!currentSwipeItem.value || assigningTo.value) return;
	assigningTo.value = channelId;
	try {
		await addMediaToChannel(channelId, currentSwipeItem.value.id);
		toast.success('Added to channel');
		dismissActionsMenu();
	} catch (e) {
		toast.error('Failed to add to channel');
	} finally {
		assigningTo.value = null;
	}
};

const swipeCardStyle = computed(() => {
	if (!swiping.value) return {};
	const rotate = swipeX.value * 0.1;
	return {
		transform: `translate(${swipeX.value}px, ${Math.min(0, swipeY.value)}px) rotate(${rotate}deg)`,
		transition: 'none'
	};
});

const swipeOverlayOpacity = computed(() => {
	const absX = Math.abs(swipeX.value);
	const absY = Math.abs(swipeY.value);
	return Math.min(1, Math.max(absX, absY) / (swipeThreshold * 2));
});

const dismissActionsMenu = () => {
	showActionsMenu.value = false;
};

// Load more in swipe mode when near end
watch(swipeIndex, (idx) => {
	if (idx >= swipeableMedia.value.length - 3 && media.value.length < totalMedia.value) {
		loadNextPage();
	}
});

onMounted(() => {
	checkMobile();
	window.addEventListener('resize', checkMobile);
	// Pre-fetch groups and channels for categorize menu
	if (vhostId?.value) {
		getGroupTree(vhostId.value).catch(() => {});
		getChannels(vhostId.value).catch(() => {});
	}
});
onBeforeUnmount(() => {
	window.removeEventListener('resize', checkMobile);
});
</script>

<template>
	<!-- ==================== MOBILE SCROLL/BROWSE MODE ==================== -->
	<div v-if="isMobile && props.mobileView === 'scroll'" class="w-full">
		<!-- Loading skeleton -->
		<div v-if="loading && totalMedia == 0" class="space-y-4 p-3">
			<div v-for="i in 4" :key="i" class="space-y-2">
				<Skeleton class="aspect-video w-full rounded-lg" />
				<Skeleton class="h-4 w-3/4" />
				<Skeleton class="h-3 w-full" />
			</div>
		</div>

		<!-- Empty state -->
		<div v-else-if="!loading && totalMedia == 0" class="flex flex-col items-center justify-center py-20 gap-4">
			<div class="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
				<ImageIcon class="h-8 w-8 text-muted-foreground" />
			</div>
			<div class="text-center">
				<p class="text-lg font-medium">No media found</p>
				<p class="text-sm text-muted-foreground mt-1">Try adjusting your filters or upload new content.</p>
			</div>
		</div>

		<!-- Vertical feed -->
		<div v-else class="space-y-3 pb-6">
			<div v-for="item in media" :key="item.id"
				class="border rounded-xl overflow-hidden bg-background hover:shadow-md transition-shadow"
				:class="{ 'opacity-50 scale-[0.98] transition-all duration-300': removingItems.has(item.id) }">
				<!-- Thumbnail (tappable to navigate) -->
				<router-link :to="'/media/' + item.id" class="block relative aspect-video bg-muted overflow-hidden">
					<media-thumb :mediaItem="item" :show-transfers="true" :size="64" class="w-full h-full" />
					<!-- File type badge -->
					<div class="absolute bottom-2 left-2">
						<span class="bg-black/60 backdrop-blur-sm text-white text-[11px] font-mono px-2 py-0.5 rounded">
							{{ item.extension?.toUpperCase() || fileTypeLabel(item.filetype) }}
						</span>
					</div>
					<!-- Moderation badge -->
					<div v-if="!isMM" class="absolute bottom-2 right-2">
						<span v-if="item.moderationStatus == 0" class="bg-yellow-500/80 text-white text-[10px] font-medium px-2 py-0.5 rounded">Pending</span>
						<span v-else-if="item.moderationStatus == 1" class="bg-green-500/80 text-white text-[10px] font-medium px-2 py-0.5 rounded">Approved</span>
						<span v-else-if="item.moderationStatus == 2" class="bg-red-500/80 text-white text-[10px] font-medium px-2 py-0.5 rounded">Denied</span>
					</div>
				</router-link>
				<!-- Info -->
				<div class="p-3">
					<router-link :to="'/media/' + item.id">
						<h3 class="text-sm font-medium leading-snug line-clamp-2">{{ item.title }}</h3>
					</router-link>
					<p v-if="item.message" class="text-xs text-muted-foreground line-clamp-2 mt-1">{{ stripHtml(item.message) }}</p>
					<div v-if="item.tags" class="flex flex-wrap gap-1 mt-2">
						<span v-for="tag in String(item.tags).split(',').slice(0, 4)" :key="tag"
							class="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-sm">{{ tag.trim() }}</span>
						<span v-if="String(item.tags).split(',').length > 4"
							class="text-[10px] text-muted-foreground/60">+{{ String(item.tags).split(',').length - 4 }}</span>
					</div>
					<div class="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
						<UserRound class="h-3 w-3 flex-shrink-0" />
						<span class="truncate">{{ item.user_firstname || item.user_email || 'Unknown' }}</span>
						<span class="text-muted-foreground/30">·</span>
						<span class="tabular-nums whitespace-nowrap cursor-pointer hover:text-foreground transition-colors" @click.stop="toggleTimeFormat" :title="timeFormatMode === 'relative' ? 'Click for absolute time' : 'Click for relative time'">{{ formatTime(item.upload) }}</span>
						<template v-if="item.groups && item.groups[0]">
							<span class="text-muted-foreground/30">·</span>
							<span class="inline-flex items-center gap-0.5 truncate">
								<FolderOpen class="h-3 w-3 flex-shrink-0" />{{ truncateText(item.groups[0].name, 15) }}
							</span>
						</template>
					</div>
				</div>
				<!-- Moderation actions bar -->
				<div v-if="!isMM" class="flex items-center justify-between px-3 py-2 border-t bg-muted/30">
					<div class="flex items-center gap-2">
						<button v-if="item.moderationStatus != 1"
							class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-green-500/10 text-green-600 hover:bg-green-500/20 active:scale-95 transition-all"
							@click.prevent.stop="moderateMedia(item.id, 1)">
							<CircleCheck class="h-3.5 w-3.5" /> Approve
						</button>
						<button v-if="item.moderationStatus != 2"
							class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-orange-500/10 text-orange-600 hover:bg-orange-500/20 active:scale-95 transition-all"
							@click.prevent.stop="moderateMedia(item.id, 2)">
							<CircleX class="h-3.5 w-3.5" /> Deny
						</button>
					</div>
					<button
						class="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-xs text-muted-foreground hover:bg-red-500/10 hover:text-red-600 active:scale-95 transition-all"
						@click.prevent.stop="trashMedia(item.id, false)">
						<Trash2 class="h-3.5 w-3.5" />
					</button>
				</div>
			</div>
		</div>

		<!-- Infinite scroll sentinel -->
		<div ref="scrollSentinel" class="py-4 flex justify-center">
			<div v-if="loadingMore && media.length < totalMedia" class="flex items-center gap-2 text-sm text-muted-foreground">
				<div class="h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-primary"></div>
				Loading more...
			</div>
			<div v-else-if="media.length >= totalMedia && totalMedia > 0" class="text-xs text-muted-foreground/50">
				All {{ totalMedia }} items loaded
			</div>
		</div>
	</div>

	<!-- ==================== MOBILE SWIPE MODE ==================== -->
	<div v-else-if="isMobile" class="relative w-full" style="height: calc(100vh - 120px);">
		<!-- Loading -->
		<div v-if="loading && totalMedia == 0" class="flex items-center justify-center h-full">
			<div class="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary"></div>
		</div>
		<!-- Empty -->
		<div v-else-if="!loading && totalMedia == 0" class="flex flex-col items-center justify-center h-full gap-4">
			<div class="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
				<ImageIcon class="h-8 w-8 text-muted-foreground" />
			</div>
			<p class="text-lg font-medium">No media found</p>
			<p class="text-sm text-muted-foreground">Try adjusting your filters or upload new content.</p>
		</div>
		<!-- Swipe card stack -->
		<div v-else class="absolute inset-0 flex items-center justify-center">
			<!-- Next card (behind) -->
			<div v-if="nextSwipeItem" class="absolute inset-x-5 top-5 bottom-5 rounded-2xl overflow-hidden bg-background shadow-sm ring-1 ring-black/5 scale-[0.95] opacity-50">
				<div class="relative w-full h-3/5 bg-muted">
					<media-thumb :mediaItem="nextSwipeItem" :show-transfers="true" :size="64" class="w-full h-full" />
				</div>
				<div class="p-4">
					<h3 class="font-medium line-clamp-1 text-sm">{{ nextSwipeItem.title }}</h3>
				</div>
			</div>

			<!-- Current card -->
			<div v-if="currentSwipeItem"
				class="absolute inset-x-3 top-3 bottom-3 rounded-2xl overflow-hidden bg-background shadow-xl ring-1 ring-black/5 z-10"
				:style="swipeCardStyle"
				:class="{ 'transition-all duration-300 ease-out': !swiping }"
				@touchstart="onTouchStart"
				@touchmove.prevent="onTouchMove"
				@touchend="onTouchEnd">

				<!-- Direction indicators — frosted pill overlays -->
				<div v-if="swiping" class="absolute inset-0 z-20 pointer-events-none">
					<!-- Right = Approve -->
					<div v-if="swipeDirection === 'right'"
						class="absolute top-6 left-5 flex items-center gap-2 bg-emerald-500/90 backdrop-blur-md text-white px-4 py-2 rounded-full shadow-lg -rotate-6"
						:style="{ opacity: swipeOverlayOpacity }">
						<ThumbsUp class="h-5 w-5" />
						<span class="text-sm font-semibold tracking-wide">Approve</span>
					</div>
					<!-- Left = Deny -->
					<div v-if="swipeDirection === 'left'"
						class="absolute top-6 right-5 flex items-center gap-2 bg-rose-500/90 backdrop-blur-md text-white px-4 py-2 rounded-full shadow-lg rotate-6"
						:style="{ opacity: swipeOverlayOpacity }">
						<ThumbsDown class="h-5 w-5" />
						<span class="text-sm font-semibold tracking-wide">Deny</span>
					</div>
					<!-- Up = Categorize -->
					<div v-if="swipeDirection === 'up'"
						class="absolute top-6 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-blue-500/90 backdrop-blur-md text-white px-4 py-2 rounded-full shadow-lg"
						:style="{ opacity: swipeOverlayOpacity }">
						<Grid3X3 class="h-5 w-5" />
						<span class="text-sm font-semibold tracking-wide">Categorize</span>
					</div>
					<!-- Down = Actions -->
					<div v-if="swipeDirection === 'down'"
						class="absolute bottom-24 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-violet-500/90 backdrop-blur-md text-white px-4 py-2 rounded-full shadow-lg"
						:style="{ opacity: swipeOverlayOpacity }">
						<Ellipsis class="h-5 w-5" />
						<span class="text-sm font-semibold tracking-wide">More</span>
					</div>
				</div>

				<!-- Media thumbnail -->
				<router-link :to="'/media/' + currentSwipeItem.id" class="block relative w-full h-3/5 bg-muted">
					<media-thumb :mediaItem="currentSwipeItem" :show-transfers="true" :size="64" class="w-full h-full" />
					<!-- Top gradient for counter readability -->
					<div class="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/30 to-transparent pointer-events-none"></div>
					<!-- File type badge -->
					<div class="absolute bottom-3 left-3">
						<span class="bg-black/50 backdrop-blur-md text-white text-[11px] font-medium tracking-wider px-2.5 py-1 rounded-full">
							{{ currentSwipeItem.extension?.toUpperCase() || fileTypeLabel(currentSwipeItem.filetype) }}
						</span>
					</div>
					<!-- Moderation badge -->
					<div class="absolute bottom-3 right-3">
						<span v-if="currentSwipeItem.moderationStatus == 0" class="bg-amber-500/90 backdrop-blur-md text-white text-[11px] font-medium px-2.5 py-1 rounded-full">Pending</span>
						<span v-else-if="currentSwipeItem.moderationStatus == 1" class="bg-emerald-500/90 backdrop-blur-md text-white text-[11px] font-medium px-2.5 py-1 rounded-full">Approved</span>
						<span v-else-if="currentSwipeItem.moderationStatus == 2" class="bg-rose-500/90 backdrop-blur-md text-white text-[11px] font-medium px-2.5 py-1 rounded-full">Denied</span>
					</div>
				</router-link>

				<!-- Info section -->
				<div class="p-4 h-2/5 overflow-y-auto">
					<h3 class="font-semibold text-base leading-snug line-clamp-2">{{ currentSwipeItem.title }}</h3>
					<p v-if="currentSwipeItem.message" class="text-sm text-muted-foreground line-clamp-3 mt-1.5">{{ stripHtml(currentSwipeItem.message) }}</p>
					<div v-if="currentSwipeItem.tags" class="flex flex-wrap gap-1.5 mt-2.5">
						<span v-for="tag in String(currentSwipeItem.tags).split(',').slice(0, 5)" :key="tag"
							class="text-[11px] bg-muted text-muted-foreground px-2 py-0.5 rounded-full">{{ tag.trim() }}</span>
					</div>
					<div class="flex items-center gap-2 mt-2.5 text-sm text-muted-foreground">
						<UserRound class="h-3.5 w-3.5" />
						<span>{{ currentSwipeItem.user_firstname || currentSwipeItem.user_email || 'Unknown' }}</span>
						<span class="text-muted-foreground/30">·</span>
						<span class="tabular-nums cursor-pointer hover:text-foreground transition-colors" @click.stop="toggleTimeFormat" :title="timeFormatMode === 'relative' ? 'Click for absolute time' : 'Click for relative time'">{{ formatTime(currentSwipeItem.upload) }}</span>
					</div>
					<div v-if="currentSwipeItem.groups && currentSwipeItem.groups[0]" class="mt-2">
						<span class="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
							<Folder class="h-3 w-3" />{{ currentSwipeItem.groups[0].name }}
						</span>
					</div>
				</div>
			</div>

			<!-- No more items -->
			<div v-else-if="!loading" class="text-center py-20">
				<div class="h-16 w-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
					<Check class="h-8 w-8 text-muted-foreground" />
				</div>
				<p class="text-lg font-semibold">All caught up!</p>
				<p class="text-sm text-muted-foreground mt-1">No more media to review.</p>
			</div>
		</div>

		<!-- Bottom action bar — frosted glass pill -->
		<div v-if="currentSwipeItem" class="absolute bottom-5 left-0 right-0 flex justify-center z-20 px-6">
			<div class="flex items-center gap-3 bg-background/80 backdrop-blur-xl border shadow-lg rounded-full px-3 py-2">
				<button @click="moderateMedia(currentSwipeItem!.id, 2)"
					class="h-12 w-12 rounded-full bg-rose-500 text-white flex items-center justify-center active:scale-90 transition-all shadow-md shadow-rose-500/25 hover:shadow-rose-500/40"
					title="Deny">
					<ThumbsDown class="h-5 w-5" />
				</button>
				<button @click="showActionsMenu = true; swipeActionMode = 'categorize'"
					class="h-10 w-10 rounded-full bg-muted hover:bg-blue-500/15 text-muted-foreground hover:text-blue-600 flex items-center justify-center active:scale-90 transition-all"
					title="Categorize">
					<Grid3X3 class="h-4.5 w-4.5" />
				</button>
				<button @click="showActionsMenu = true; swipeActionMode = 'actions'"
					class="h-10 w-10 rounded-full bg-muted hover:bg-violet-500/15 text-muted-foreground hover:text-violet-600 flex items-center justify-center active:scale-90 transition-all"
					title="More actions">
					<Ellipsis class="h-4.5 w-4.5" />
				</button>
				<button @click="moderateMedia(currentSwipeItem!.id, 1)"
					class="h-12 w-12 rounded-full bg-emerald-500 text-white flex items-center justify-center active:scale-90 transition-all shadow-md shadow-emerald-500/25 hover:shadow-emerald-500/40"
					title="Approve">
					<ThumbsUp class="h-5 w-5" />
				</button>
			</div>
		</div>

		<!-- Card counter — top right pill -->
		<div v-if="swipeableMedia.length > 0" class="absolute top-1.5 right-3 z-20">
			<span class="text-[11px] font-medium text-muted-foreground bg-background/70 backdrop-blur-md px-2.5 py-1 rounded-full border border-border/50 tabular-nums">
				{{ swipeIndex + 1 }} / {{ totalMedia }}
			</span>
		</div>

		<!-- Actions overlay menu -->
		<div v-if="showActionsMenu && currentSwipeItem" class="absolute inset-0 z-30 bg-black/40 backdrop-blur-sm flex items-end" @click.self="dismissActionsMenu">
			<div class="w-full bg-background rounded-t-3xl p-5 pb-8 max-h-[60vh] overflow-y-auto shadow-2xl">
				<div class="w-8 h-1 bg-border rounded-full mx-auto mb-5"></div>

				<template v-if="swipeActionMode === 'categorize'">
					<!-- Current group info -->
					<div v-if="currentSwipeItem.groups && currentSwipeItem.groups[0]" class="mb-4 p-3 bg-muted/40 rounded-xl border border-border/50">
						<p class="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Currently in</p>
						<p class="text-sm font-semibold flex items-center gap-1.5 mt-1">
							<Folder class="h-3.5 w-3.5 text-blue-500" />{{ currentSwipeItem.groups[0].name }}
						</p>
					</div>

					<!-- Groups section -->
					<h3 class="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Groups</h3>
					<div v-if="flatGroups.length === 0" class="flex items-center justify-center py-4">
						<Loader2 class="h-5 w-5 animate-spin text-muted-foreground" />
					</div>
					<div v-else class="space-y-0.5 mb-5 max-h-[25vh] overflow-y-auto">
						<button v-for="group in flatGroups" :key="'g-' + group.id"
							class="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-muted active:bg-muted/80 transition-colors text-left"
							:style="{ paddingLeft: (12 + group.depth * 16) + 'px' }"
							:disabled="assigningTo === group.id"
							@click="handleAddToGroup(group.id)">
							<div class="h-7 w-7 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
								<Folder class="h-3.5 w-3.5 text-blue-500" />
							</div>
							<span class="text-sm truncate flex-1">{{ group.name }}</span>
							<Loader2 v-if="assigningTo === group.id" class="h-4 w-4 animate-spin text-primary flex-shrink-0" />
						</button>
					</div>

					<!-- Channels section -->
					<h3 class="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Channels</h3>
					<div v-if="flatChannels.length === 0" class="flex items-center justify-center py-4">
						<Loader2 class="h-5 w-5 animate-spin text-muted-foreground" />
					</div>
					<div v-else class="space-y-0.5 max-h-[25vh] overflow-y-auto">
						<button v-for="channel in flatChannels" :key="'c-' + channel.id"
							class="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-muted active:bg-muted/80 transition-colors text-left"
							:style="{ paddingLeft: (12 + channel.depth * 16) + 'px' }"
							:disabled="assigningTo === channel.id"
							@click="handleAddToChannel(channel.id)">
							<div class="h-7 w-7 rounded-lg bg-violet-500/10 flex items-center justify-center flex-shrink-0">
								<Rss class="h-3.5 w-3.5 text-violet-500" />
							</div>
							<span class="text-sm truncate flex-1">{{ channel.name }}</span>
							<Loader2 v-if="assigningTo === channel.id" class="h-4 w-4 animate-spin text-primary flex-shrink-0" />
						</button>
					</div>
				</template>

				<template v-else>
					<h3 class="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">Actions</h3>
					<div class="space-y-1">
						<button class="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted active:bg-muted/80 transition-colors text-left" @click="dismissActionsMenu">
							<div class="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0">
								<Send class="h-4 w-4 text-blue-500" />
							</div>
							<div>
								<p class="text-sm font-medium">Send for Review</p>
								<p class="text-xs text-muted-foreground">Share with reviewers</p>
							</div>
						</button>
						<template v-if="transferList.length > 0">
							<button v-for="(transfer, tIndex) in transferList" :key="tIndex"
								class="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted active:bg-muted/80 transition-colors text-left"
								@click="dismissActionsMenu">
								<div class="h-9 w-9 rounded-xl bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
									<ArrowUpFromLine class="h-4 w-4 text-indigo-500" />
								</div>
								<div>
									<p class="text-sm font-medium">Transfer to {{ transfer.name }}</p>
									<p class="text-xs text-muted-foreground">Send to external platform</p>
								</div>
							</button>
						</template>
						<button class="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted active:bg-muted/80 transition-colors text-left" @click="dismissActionsMenu">
							<div class="h-9 w-9 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
								<ArrowDownToLine class="h-4 w-4 text-muted-foreground" />
							</div>
							<div>
								<p class="text-sm font-medium">Download</p>
								<p class="text-xs text-muted-foreground">Save original file</p>
							</div>
						</button>
						<button class="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted active:bg-muted/80 transition-colors text-left" @click="dismissActionsMenu">
							<div class="h-9 w-9 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
								<Sparkles class="h-4 w-4 text-amber-500" />
							</div>
							<div>
								<p class="text-sm font-medium">AI Analysis</p>
								<p class="text-xs text-muted-foreground">Content analysis, transcription, detection</p>
							</div>
						</button>
						<div class="my-2 border-t border-border/50"></div>
						<button class="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-rose-500/10 active:bg-rose-500/15 transition-colors text-left"
							@click="trashMedia(currentSwipeItem!.id, false); dismissActionsMenu()">
							<div class="h-9 w-9 rounded-xl bg-rose-500/10 flex items-center justify-center flex-shrink-0">
								<Trash2 class="h-4 w-4 text-rose-500" />
							</div>
							<div>
								<p class="text-sm font-medium text-rose-600">Trash</p>
								<p class="text-xs text-rose-400">Move to trash</p>
							</div>
						</button>
					</div>
				</template>
			</div>
		</div>
	</div>

	<!-- ==================== LIST VIEW ==================== -->
	<div v-else-if="type == 'list'">
		<div v-if="loading && totalMedia == 0" class="flex items-center justify-center py-20">
			<div class="flex flex-col items-center gap-3">
				<div class="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary"></div>
				<p class="text-sm text-muted-foreground">Loading media...</p>
			</div>
		</div>
		<div v-else class="w-full">
			<table class="w-full text-sm">
				<thead>
					<tr class="border-b text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
						<th class="w-8 py-2 px-2"></th>
						<th class="w-16 py-2 px-1"></th>
						<th class="py-2 px-3">Title</th>
						<th class="py-2 px-3 hidden lg:table-cell">User</th>
						<th class="py-2 px-3 hidden md:table-cell">Group</th>
						<th class="py-2 px-3 hidden lg:table-cell">Type</th>
						<th class="py-2 px-3 text-right">Uploaded</th>
						<th class="w-10 py-2 px-2"></th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="item in media" :key="item.id"
						class="group border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer"
						:class="{ 'bg-primary/5': isSelected(item.id), 'opacity-50': removingItems.has(item.id) }"
						@click="isMobile ? router.push('/media/' + item.id) : toggleSelected(item)">
						<td class="py-2 px-2">
							<Checkbox :model-value="isSelected(item.id)" @click.stop @update:model-value="toggleSelected(item)" class="data-[state=checked]:bg-primary" />
						</td>
						<td class="py-2 px-1">
							<router-link :to="'/media/' + item.id" @click.stop>
								<div class="w-12 h-8 rounded overflow-hidden bg-muted">
									<img :src="item.publicUrl + '/11'" :alt="item.title" class="w-full h-full object-cover" />
								</div>
							</router-link>
						</td>
						<td class="py-2 px-3">
							<router-link :to="'/media/' + item.id" @click.stop class="hover:text-primary transition-colors">
								<span class="font-medium line-clamp-1">{{ item.title }}</span>
							</router-link>
							<p v-if="item.message" class="text-xs text-muted-foreground line-clamp-1 mt-0.5">{{ stripHtml(item.message) }}</p>
						</td>
						<td class="py-2 px-3 hidden lg:table-cell">
							<router-link v-if="item.uid" :to="'/member/' + item.uid" @click.stop class="text-xs text-muted-foreground hover:text-primary transition-colors">
								{{ item.user_firstname || item.user_email }}
							</router-link>
						</td>
						<td class="py-2 px-3 hidden md:table-cell">
							<router-link v-if="item.groups && item.groups[0]" :to="'/groups/' + item.groups[0].id" @click.stop class="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors">
								<FolderOpen class="h-3 w-3" />{{ item.groups[0].name }}
							</router-link>
							<span v-else class="text-xs text-muted-foreground">—</span>
						</td>
						<td class="py-2 px-3 hidden lg:table-cell">
							<span class="bg-muted px-1.5 py-0.5 rounded-sm text-xs font-mono">{{ fileTypeLabel(item.filetype) }}</span>
						</td>
						<td class="py-2 px-3 text-right">
							<span class="text-xs text-muted-foreground tabular-nums cursor-pointer hover:text-foreground transition-colors" @click.stop="toggleTimeFormat" :title="timeFormatMode === 'relative' ? 'Click for absolute time' : 'Click for relative time'">{{ formatTime(item.upload) }}</span>
						</td>
						<td class="py-2 px-2">
							<div class="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" @click.stop>
								<button v-if="!isMM && item.moderationStatus != 1" class="p-1 rounded hover:bg-green-500/10 hover:text-green-600 text-muted-foreground transition-colors" @click.prevent="moderateMedia(item.id, 1)">
									<CircleCheck class="h-3.5 w-3.5" />
								</button>
								<button v-if="!isMM && item.moderationStatus != 2" class="p-1 rounded hover:bg-orange-500/10 hover:text-orange-600 text-muted-foreground transition-colors" @click.prevent="moderateMedia(item.id, 2)">
									<CircleX class="h-3.5 w-3.5" />
								</button>
								<button v-if="item.status < 100" class="p-1 rounded hover:bg-red-500/10 hover:text-red-600 text-muted-foreground transition-colors" @click.prevent="trashMedia(item.id, false)">
									<Trash2 class="h-3.5 w-3.5" />
								</button>
							</div>
						</td>
					</tr>
				</tbody>
			</table>

			<!-- Infinite scroll sentinel + loading indicator for list -->
			<div ref="listScrollSentinel" class="py-4 flex justify-center">
				<div v-if="loadingMore && media.length < totalMedia" class="flex items-center gap-2 text-sm text-muted-foreground">
					<div class="h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-primary"></div>
					Loading more...
				</div>
			</div>
		</div>
	</div>

	<!-- ==================== GRID VIEW ==================== -->
	<div v-else-if="type == 'grid' && userInfo && vhostId && userInfo.permissions" class="mb-10">
		<!-- Loading skeleton -->
		<div v-if="loading && totalMedia == 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
			<div v-for="i in 8" :key="i" class="space-y-2">
				<Skeleton class="aspect-video w-full rounded-lg" />
				<Skeleton class="h-4 w-3/4" />
				<Skeleton class="h-3 w-full" />
				<Skeleton class="h-3 w-1/2" />
			</div>
		</div>

		<!-- Empty state -->
		<div v-else-if="!loading && totalMedia == 0" class="flex flex-col items-center justify-center py-20 gap-4">
			<div class="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
				<ImageIcon class="h-8 w-8 text-muted-foreground" />
			</div>
			<div class="text-center">
				<p class="text-lg font-medium">No media found</p>
				<p class="text-sm text-muted-foreground mt-1">Try adjusting your filters or upload new content.</p>
			</div>
		</div>

		<!-- Grid -->
		<div v-else>
			<TransitionGroup
				v-if="media.length > 0"
				class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
				tag="div"
				name="fade">
				<div v-for="(mediaItem, index) in media" :key="mediaItem.id"
					class="group relative"
					:class="{ 'opacity-50 scale-95': removingItems.has(mediaItem.id) }">
					<div
						class="relative border rounded-lg overflow-hidden bg-background transition-all duration-150 cursor-pointer hover:shadow-md"
						:class="{
							'ring-2 ring-primary ring-offset-2': isSelected(mediaItem.id),
							'ring-1 ring-blue-400': draggedItemId == mediaItem.id,
						}"
						draggable="true"
						@dragstart="handleDragStart(mediaItem, $event)"
						@dragover.prevent
						@dragend="onDragEnd"
						@click="isMobile ? router.push('/media/' + mediaItem.id) : toggleSelected(mediaItem)">

						<!-- Thumbnail -->
						<div class="relative aspect-video bg-muted overflow-hidden">
							<router-link :to="'/media/' + mediaItem.id" @click.stop>
								<media-thumb :mediaItem="mediaItem" :show-transfers="true" :key="mediaItem.id" :percentage="mediaItem.statusInfo?.conversion_percentage" :size="64" />
							</router-link>

							<!-- Checkbox overlay (top-left) -->
							<div class="absolute top-2 left-2 z-10" :class="isSelected(mediaItem.id) ? 'opacity-100' : (isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100')" @click.stop>
								<Checkbox :model-value="isSelected(mediaItem.id)" @update:model-value="toggleSelected(mediaItem)" class="h-5 w-5 bg-background/80 backdrop-blur-sm border-background/50 data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
							</div>

							<!-- Quick actions overlay (top-right) -->
							<div class="absolute top-2 right-2 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" @click.stop>
								<button v-if="!isMM && mediaItem.moderationStatus != 1"
									class="h-7 w-7 rounded-md bg-background/80 backdrop-blur-sm flex items-center justify-center hover:bg-green-500 hover:text-white text-muted-foreground transition-colors"
									@click.prevent="moderateMedia(mediaItem.id, 1)">
									<CircleCheck class="h-3.5 w-3.5" />
								</button>
								<button v-if="!isMM && mediaItem.moderationStatus != 2"
									class="h-7 w-7 rounded-md bg-background/80 backdrop-blur-sm flex items-center justify-center hover:bg-orange-500 hover:text-white text-muted-foreground transition-colors"
									@click.prevent="moderateMedia(mediaItem.id, 2)">
									<CircleX class="h-3.5 w-3.5" />
								</button>
								<button v-if="mediaItem.status < 100"
									class="h-7 w-7 rounded-md bg-background/80 backdrop-blur-sm flex items-center justify-center hover:bg-red-500 hover:text-white text-muted-foreground transition-colors"
									@click.prevent="trashMedia(mediaItem.id, false)">
									<Trash2 class="h-3.5 w-3.5" />
								</button>
							</div>

							<!-- File type badge (bottom-left) -->
							<div class="absolute bottom-2 left-2">
								<span class="bg-black/60 backdrop-blur-sm text-white text-[11px] font-mono px-2 py-0.5 rounded">
									{{ mediaItem.extension?.toUpperCase() || fileTypeLabel(mediaItem.filetype) }}
								</span>
							</div>

							<!-- Moderation status badge (bottom-right) -->
							<div class="absolute bottom-2 right-2" v-if="!isMM">
								<span v-if="mediaItem.moderationStatus == 0" class="bg-yellow-500/80 backdrop-blur-sm text-white text-[10px] font-medium px-2 py-0.5 rounded">Pending</span>
								<span v-else-if="mediaItem.moderationStatus == 1" class="bg-green-500/80 backdrop-blur-sm text-white text-[10px] font-medium px-2 py-0.5 rounded">Approved</span>
								<span v-else-if="mediaItem.moderationStatus == 2" class="bg-red-500/80 backdrop-blur-sm text-white text-[10px] font-medium px-2 py-0.5 rounded">Denied</span>
							</div>
						</div>

						<!-- Info -->
						<div class="p-3">
							<div class="flex items-start justify-between gap-2">
								<router-link :to="'/media/' + mediaItem.id" @click.stop class="hover:text-primary transition-colors min-w-0 flex-1">
									<h3 class="text-sm font-medium leading-snug line-clamp-2">{{ mediaItem.title }}</h3>
								</router-link>
								<div @click.stop class="flex-shrink-0">
									<DropdownMenu>
										<DropdownMenuTrigger class="h-7 w-7 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-muted transition-all text-muted-foreground">
											<MoreHorizontal class="h-4 w-4" />
										</DropdownMenuTrigger>
										<DropdownMenuContent align="end" class="w-44">
											<DropdownMenuItem class="text-xs gap-2"><ArrowDownToLine class="h-3.5 w-3.5" /> Download</DropdownMenuItem>
											<DropdownMenuItem v-if="!isMM" class="text-xs gap-2" @click="moderateMedia(mediaItem.id, 1)"><CircleCheck class="h-3.5 w-3.5" /> Approve</DropdownMenuItem>
											<DropdownMenuItem v-if="!isMM" class="text-xs gap-2" @click="moderateMedia(mediaItem.id, 2)"><CircleX class="h-3.5 w-3.5" /> Deny</DropdownMenuItem>
											<DropdownMenuItem v-if="!isMM" class="text-xs gap-2" @click="trashMedia(mediaItem.id, false)"><Trash2 class="h-3.5 w-3.5" /> Trash</DropdownMenuItem>
											<DropdownMenuItem v-if="isMM" class="text-xs gap-2"><Trash2 class="h-3.5 w-3.5" /> Archive</DropdownMenuItem>
											<DropdownMenuSeparator />
											<DropdownMenuSub>
												<DropdownMenuSubTrigger class="text-xs gap-2"><Sparkles class="h-3.5 w-3.5" /> AI</DropdownMenuSubTrigger>
												<DropdownMenuSubContent>
													<DropdownMenuItem v-if="mediaItem.filetype == 2" class="text-xs">Transcribe</DropdownMenuItem>
													<DropdownMenuItem class="text-xs">Content Analysis</DropdownMenuItem>
													<DropdownMenuItem class="text-xs">Celeb Detect</DropdownMenuItem>
													<DropdownMenuItem class="text-xs">Face Detection</DropdownMenuItem>
													<DropdownMenuItem v-if="mediaItem.filetype == 2" class="text-xs">Summarize</DropdownMenuItem>
												</DropdownMenuSubContent>
											</DropdownMenuSub>
											<DropdownMenuItem class="text-xs gap-2"><ImageIcon class="h-3.5 w-3.5" /> Replace Thumbnail</DropdownMenuItem>
											<DropdownMenuSeparator />
											<DropdownMenuSub v-if="transferList.length > 0">
												<DropdownMenuSubTrigger class="text-xs gap-2"><ArrowUpFromLine class="h-3.5 w-3.5" /> Transfer</DropdownMenuSubTrigger>
												<DropdownMenuSubContent>
													<template v-for="(transfer, tIndex) in transferList" :key="tIndex">
														<DropdownMenuItem v-if="mediaItem.filetype == transfer.filetype || transfer.filetype == 0" class="text-xs">{{ transfer.name }}</DropdownMenuItem>
													</template>
												</DropdownMenuSubContent>
											</DropdownMenuSub>
											<DropdownMenuSub>
												<DropdownMenuSubTrigger class="text-xs gap-2"><Send class="h-3.5 w-3.5" /> Share</DropdownMenuSubTrigger>
												<DropdownMenuSubContent>
													<DropdownMenuItem class="text-xs" @click="copyShareLink(mediaItem, 'review')">For Review</DropdownMenuItem>
													<DropdownMenuItem class="text-xs" @click="copyShareLink(mediaItem, 'asset')">Asset Link</DropdownMenuItem>
												</DropdownMenuSubContent>
											</DropdownMenuSub>
										</DropdownMenuContent>
									</DropdownMenu>
								</div>
							</div>

							<!-- Description / message preview -->
							<p v-if="mediaItem.message" class="text-xs text-muted-foreground line-clamp-2 mt-1.5 leading-relaxed">{{ stripHtml(mediaItem.message) }}</p>

							<!-- Tags -->
							<div v-if="mediaItem.tags" class="flex flex-wrap gap-1 mt-2">
								<span v-for="tag in String(mediaItem.tags).split(',').slice(0, 3)" :key="tag"
									class="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-sm">{{ tag.trim() }}</span>
								<span v-if="String(mediaItem.tags).split(',').length > 3"
									class="text-[10px] text-muted-foreground/60">+{{ String(mediaItem.tags).split(',').length - 3 }}</span>
							</div>

							<!-- Meta row: user, time -->
							<div class="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
								<router-link v-if="mediaItem.uid" :to="'/member/' + mediaItem.uid" @click.stop class="flex items-center gap-1 hover:text-primary truncate transition-colors">
									<UserRound class="h-3 w-3 flex-shrink-0" />
									{{ mediaItem.user_firstname || mediaItem.user_email || 'Unknown' }}
								</router-link>
								<span class="text-muted-foreground/30">·</span>
								<span class="tabular-nums whitespace-nowrap cursor-pointer hover:text-foreground transition-colors" @click.stop="toggleTimeFormat" :title="timeFormatMode === 'relative' ? 'Click for absolute time' : 'Click for relative time'">{{ formatTime(mediaItem.upload) }}</span>
								<template v-if="mediaItem.hits && mediaItem.hits > 0">
									<span class="text-muted-foreground/30">·</span>
									<span class="flex items-center gap-0.5 tabular-nums" :title="mediaItem.hits + ' views'">
										<Eye class="h-3 w-3" />{{ mediaItem.hits.toLocaleString() }}
									</span>
								</template>
							</div>

							<!-- Group & Channel tags -->
							<div v-if="(mediaItem.groups && mediaItem.groups[0]) || (mediaItem.channel && mediaItem.channel > 0)" class="flex flex-wrap gap-1 mt-2">
								<router-link v-if="mediaItem.groups && mediaItem.groups[0]" :to="'/groups/' + mediaItem.groups[0].id" @click.stop
									class="inline-flex items-center gap-1 text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded hover:text-primary transition-colors">
									<FolderOpen class="h-3 w-3" />{{ truncateText(mediaItem.groups[0].name, 20) }}
								</router-link>
								<span v-if="mediaItem.groups && mediaItem.groups.length > 1"
									class="inline-flex items-center text-[10px] text-muted-foreground/60 bg-muted px-1.5 py-0.5 rounded"
									:title="mediaItem.groups.slice(1).map((g) => { return g.name }).join(', ')">+{{ mediaItem.groups.length - 1 }}</span>
								<span v-if="mediaItem.channel && mediaItem.channel > 0 && getChannelName(mediaItem.channel)"
									class="inline-flex items-center gap-1 text-[10px] text-muted-foreground bg-blue-50 dark:bg-blue-950 px-1.5 py-0.5 rounded">
									<Rss class="h-3 w-3" />{{ truncateText(getChannelName(mediaItem.channel) || '', 20) }}
								</span>
							</div>
						</div>
					</div>
				</div>
			</TransitionGroup>

			<!-- Infinite scroll sentinel + loading indicator -->
			<div ref="scrollSentinel" class="py-6 flex justify-center">
				<div v-if="loadingMore && media.length < totalMedia" class="flex items-center gap-2 text-sm text-muted-foreground">
					<div class="h-5 w-5 animate-spin rounded-full border-2 border-muted border-t-primary"></div>
					Loading more...
				</div>
				<div v-else-if="media.length >= totalMedia && totalMedia > 0" class="text-xs text-muted-foreground/50">
					All {{ totalMedia }} items loaded
				</div>
			</div>
		</div>

		<!-- Drag preview -->
		<div ref="dragPreview" class="drag-preview fixed top-0 left-0 pointer-events-none opacity-0 z-[1000]">
			<div class="bg-background border rounded-md shadow-lg overflow-hidden w-48" v-if="selectedMediaObjects[0]">
				<media-thumb :mediaItem="selectedMediaObjects[0]" />
				<div class="p-2">
					<p class="text-xs font-medium truncate">{{ selectedMediaObjects[0]?.title }}</p>
					<p v-if="selectedMedia.length > 1" class="text-xs text-primary mt-0.5">+{{ selectedMedia.length - 1 }} more</p>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.drag-preview {
	transform: translate(-50%, -50%);
	transition: opacity 0.2s ease;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s, transform 0.3s;
}
.fade-enter-from {
	opacity: 0;
	transform: scale(0.95);
}
.fade-leave-to {
	opacity: 0;
	transform: scale(0.95);
}
</style>
